import type { InstrumentSpec, Side } from './types'

// Risk-based position sizing (pure). Given a stop price and a risk %, compute
// the lot size so that hitting the stop loses ~risk% of balance. This is what
// makes the journal's R-multiple / expectancy stats honest: every trade risks
// roughly 1R, so win-rate × payoff becomes a meaningful expectancy.

export interface RiskSizeInput {
  balance: number
  riskPct: number // percent, e.g. 2 for 2%
  entryPrice: number
  slPrice: number
  side: Side
  spec: InstrumentSpec
}

export interface RiskSize {
  valid: boolean
  reason?: string
  slDistance: number // |entry - sl| in price units
  lots: number
  riskTarget: number // intended risk in account ccy (balance × pct)
  lossAtSl: number // actual loss at the rounded lot size
}

export function sizeByRisk(i: RiskSizeInput): RiskSize {
  const dir = i.side === 'buy' ? i.entryPrice - i.slPrice : i.slPrice - i.entryPrice
  const slDistance = round4(Math.abs(dir))
  const riskTarget = round2((i.balance * i.riskPct) / 100)
  const base: RiskSize = { valid: false, slDistance, lots: 0, riskTarget, lossAtSl: 0 }
  if (!(i.entryPrice > 0) || !Number.isFinite(i.slPrice)) return { ...base, reason: '価格未確定' }
  if (dir <= 0)
    return { ...base, reason: i.side === 'buy' ? 'SLはエントリーより下に' : 'SLはエントリーより上に' }
  if (riskTarget <= 0) return { ...base, reason: 'リスク%を入れて' }

  const vpp = i.spec.valuePerPointPerLot
  const step = i.spec.lotStep || 0.01
  const raw = riskTarget / (slDistance * vpp)
  let lots = round4(Math.floor(raw / step + 1e-9) * step) // floor to step → never exceed target
  if (lots < i.spec.minLot) lots = i.spec.minLot // clamp up (risk slightly over target)
  const lossAtSl = round2(slDistance * vpp * lots)
  return { valid: true, slDistance, lots, riskTarget, lossAtSl }
}

/** TP price from a reward:risk multiple (R) and the SL distance. */
export function tpFromR(side: Side, entryPrice: number, slDistance: number, r: number): number {
  return side === 'buy' ? entryPrice + r * slDistance : entryPrice - r * slDistance
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}
function round4(n: number): number {
  return Math.round(n * 10000) / 10000
}
